import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "styles/legacy/UploadNewTitle.css";
import UploadHeader from "../components/UploadFlow/UploadHeader";
import UploadFile, {
  UploadStages,
} from "../components/UploadFlow/UploadFile";
import NewTitleMetadata from "../components/UploadFlow/NewTitleMetadata";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { getAssetById, removeAsset } from "shared/api/client/orvalHelpers";
import { assetApi } from "shared/api/client/orval";
import { parseApiError } from "shared/api/client/errorParser";

type FileFields = {
  mainVideo: File | null;
  trailer: File | null;
  heroImage: File | null;
  thumbnail: File | null;
  subtitles: File[];
};

const UploadNewTitle: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingAssetId = location.state?.assetId;
  const editingAssetStatus = location.state?.status;
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const isSavingDraftRef = useRef(false);
  const assetDeletedRef = useRef(false);
  const [assetStatus, setAssetStatus] = useState<string>(
    editingAssetStatus || ""
  );
  const [detailError, setDetailError] = useState("");
  const [loadingDetail, setLoadingDetail] = useState(Boolean(editingAssetId));
  const [files, setFiles] = useState<FileFields>({
    mainVideo: null,
    trailer: null,
    heroImage: null,
    thumbnail: null,
    subtitles: [],
  });
  const [metadata, setMetadata] = useState({
    title: "",
    language: "",
    category: "",
    genre: "",
    description: "",
    format: "",
    ageRating: "",
    comfortLevel: "",
    copyrightOwner: "",
    publisher: "",
    releaseDate: "",
    festivalParticipations: "",
    awards: "",
  });
  const [assetAndFileIds, setAssetAndFileIds] = useState({
    assetId: editingAssetId || "",
    mainVideo: undefined as string | undefined,
    trailer: undefined as string | undefined,
    heroImage: undefined as string | undefined,
    thumbnail: undefined as string | undefined,
    videoIds: [] as string[],
    imageIds: [] as string[],
    subtitleIds: [] as string[],
  });
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, boolean>
  >({});

  const MAX_SIZE_MB = {
    mainVideo: 40960, // 40 GB
    trailer: 2048, // 2 GB
    heroImage: 10, // 10 MB
    thumbnail: 10, // 10 MB
    subtitles: 2, // 2 MB
  };

  const MIN_DIMENSIONS = {
    heroImage: { width: 1920, height: 1080 },
    thumbnail: { width: 400, height: 400 },
  };

  const hasCreatedAsset = React.useRef(false);

  const [uploadStages, setUploadStages] = useState<
    UploadStages
  >({
    mainVideo: "idle",
    trailer: "idle",
    heroImage: "idle",
    thumbnail: "idle",
    subtitles: [],
  });

  const handleUploadStagesChange = React.useCallback((stages: UploadStages) => {
    setUploadStages((prev) =>
      prev.mainVideo === stages.mainVideo &&
      prev.trailer === stages.trailer &&
      prev.heroImage === stages.heroImage &&
      prev.thumbnail === stages.thumbnail &&
      prev.subtitles === stages.subtitles
        ? prev
        : {
            mainVideo: stages.mainVideo,
            trailer: stages.trailer,
            heroImage: stages.heroImage,
            thumbnail: stages.thumbnail,
            subtitles: stages.subtitles,
          }
    );
  }, []);

  const uploading = Object.values(uploadStages).some((stage) => Array.isArray(stage) ? stage.includes("uploading") : stage === "uploading");
  const savingBlocked = uploading || loadingDetail || Boolean(detailError) || ["UnderReview", "Encoding"].includes(assetStatus);

  const handleFileChange =
    (field: keyof FileFields) => (fileOrFiles: File | File[] | null) => {
      if (!fileOrFiles) {
        setFiles((prev) => ({
          ...prev,
          [field]: field === "subtitles" ? [] : null,
        }));
        return;
      }

      if (field === "subtitles") {
        // fileOrFiles must be File[]
        const filesArray = Array.isArray(fileOrFiles)
          ? fileOrFiles
          : [fileOrFiles];

        // Check size for each subtitle file
        const maxSizeBytes = MAX_SIZE_MB.subtitles * 1024 * 1024;
        const oversizedFiles = filesArray.filter(
          (file) => file.size > maxSizeBytes
        );

        if (oversizedFiles.length > 0) {
          alert(
            `Some subtitle files exceed the limit of ${MAX_SIZE_MB.subtitles}MB. Please check the file sizes.`
          );
          return;
        }

        setFiles((prev) => ({ ...prev, [field]: filesArray }));
        return;
      }

      // existing single file validations for other fields below
      const file = fileOrFiles as File;

      if (field in MAX_SIZE_MB) {
        const maxSizeBytes =
          MAX_SIZE_MB[field as keyof typeof MAX_SIZE_MB] * 1024 * 1024;
        if (file.size > maxSizeBytes) {
          alert(
            `File size exceeds the limit of ${
              MAX_SIZE_MB[field as keyof typeof MAX_SIZE_MB]
            }MB for ${field}`
          );
          return;
        }
      }

      if (field in MIN_DIMENSIONS) {
        const img = new Image();
        img.onload = () => {
          const { width, height } =
            MIN_DIMENSIONS[field as keyof typeof MIN_DIMENSIONS];
          if (img.width < width || img.height < height) {
            alert(
              `Image dimensions for ${field} must be at least ${width}x${height}px.`
            );
            return;
          }
          setFiles((prev) => ({ ...prev, [field]: file }));
          // Clear validation error when file is uploaded
          if (field === "mainVideo") {
            setValidationErrors((prev) => ({ ...prev, mainVideo: false }));
          }
          if (field === "heroImage") {
            setValidationErrors((prev) => ({ ...prev, heroImage: false }));
          }
        };
        img.onerror = () => {
          alert(`Failed to load image file for ${field}.`);
        };
        img.src = URL.createObjectURL(file);
        return;
      }

      setFiles((prev) => ({ ...prev, [field]: file }));
      // Clear validation error when file is uploaded
      if (field === "mainVideo") {
        setValidationErrors((prev) => ({ ...prev, mainVideo: false }));
      }
      if (field === "heroImage") {
        setValidationErrors((prev) => ({ ...prev, heroImage: false }));
      }
    };

  useEffect(() => {
    document.body.classList.toggle(
      "modal-open",
      showDiscardModal || showSubmitModal
    );
    return () => document.body.classList.remove("modal-open");
  }, [showDiscardModal, showSubmitModal]);

  // Function to create a new asset
  const createAsset = async () => {
    const token = getCookie("userToken");
    if (!token) {
      alert("Please log in to upload content");
      window.location.href = "/login";
      return;
    }
    try {
      // Backend returns void in schema, but actual backend likely returns assetUploadId.
      // Fallback to calling details endpoint isn't possible here; keep legacy behavior:
      // If your backend does not return assetUploadId, update swagger/spec to include it.
      const anyRes = (await (assetApi as any).postApiAssetCreate()).data;
      const id = anyRes?.assetUploadId ?? anyRes?.id;
      if (!id) throw new Error("Asset create did not return an assetUploadId.");
      return String(id);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) throw new Error("Your session has expired. Please log in again.");
      const msg = err?.response?.data?.message || err?.message || "Failed to create asset";
      throw new Error(String(msg));
    }
  };

  // On mount: if not editing, create a new asset
  useEffect(() => {
    if (
      !editingAssetId &&
      !assetAndFileIds.assetId &&
      !hasCreatedAsset.current
    ) {
      hasCreatedAsset.current = true;
      createAsset()
        .then((id) => {
          setAssetAndFileIds((prev) => ({ ...prev, assetId: id }));
        })
        .catch((err) => {
          alert(err.message || "Failed to create asset");
          hasCreatedAsset.current = false; // allow retry if failed
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingAssetId]);

  useEffect(() => {
    if (editingAssetId) {
      let active = true;
      setLoadingDetail(true);
      setDetailError("");
      // Get the detailed asset data
      getAssetById(editingAssetId)
        .then((asset) => {
          if (!active) return;
          setAssetStatus(String(asset.status || "Draft"));
          const mainVideoObj = asset.videos?.find(
            (v: any) => v.role === "MainVideo"
          );
          const trailerObj = asset.videos?.find(
            (v: any) => v.role === "Trailer"
          );
          const heroImageObj = asset.images?.find(
            (i: any) => i.role === "HeroImage"
          );
          const thumbnailObj = asset.images?.find(
            (i: any) => i.role === "Thumbnail"
          );

          // Get all video IDs
          const videoIds = [
            ...(mainVideoObj?.id ? [mainVideoObj.id.toString()] : []),
            ...(trailerObj?.id ? [trailerObj.id.toString()] : []),
            ...(asset.videoLib?.map((v: any) => v.id?.toString()) || []),
          ].filter(Boolean);

          // Get all image IDs
          const imageIds = [
            ...(heroImageObj?.id ? [heroImageObj.id.toString()] : []),
            ...(thumbnailObj?.id ? [thumbnailObj.id.toString()] : []),
            ...(asset.imageLib?.map((i: any) => i.id?.toString()) || []),
          ].filter(Boolean);

          // Get all subtitle IDs
          const subtitleIds =
            asset.subtitleLib?.map((s: any) => s.id?.toString()) || [];

          console.log("Asset loaded for editing:", {
            assetId: asset.id,
            status: asset.status,
            title: asset.title,
            allKeys: Object.keys(asset),
          });

          setMetadata({
            title: asset.title || "",
            language: asset.language || "",
            category: asset.category || "",
            genre: asset.genre || "",
            description: asset.description || "",
            format: asset.format || "",
            ageRating: asset.ageRating || "",
            comfortLevel: asset.comfortLevel || "",
            copyrightOwner: asset.copyrightOwner || "",
            publisher: asset.publisher || "",
            releaseDate: asset.releaseDate || "",
            festivalParticipations: asset.festivalParticipations || "",
            awards: asset.awards || "",
          });

          setAssetAndFileIds({
            assetId: asset.id?.toString() || "",
            mainVideo: mainVideoObj?.id?.toString(),
            trailer: trailerObj?.id?.toString(),
            heroImage: heroImageObj?.id?.toString(),
            thumbnail: thumbnailObj?.id?.toString(),
            videoIds,
            imageIds,
            subtitleIds,
          });

          setFiles({
            mainVideo: mainVideoObj
              ? Object.assign(
                  new File([""], mainVideoObj.fileName, {
                    type: mainVideoObj.format || "video/mp4",
                    lastModified: Date.now(),
                  }),
                  {
                    isPlaceholder: true,
                    originalSize: mainVideoObj.size
                      ? mainVideoObj.size * 1024 * 1024
                      : 0,
                    duration: mainVideoObj.duration || 0,
                  }
                )
              : null,
            trailer: trailerObj
              ? Object.assign(
                  new File([""], trailerObj.fileName, {
                    type: trailerObj.format || "video/mp4",
                    lastModified: Date.now(),
                  }),
                  {
                    isPlaceholder: true,
                    originalSize: trailerObj.size
                      ? trailerObj.size * 1024 * 1024
                      : 0,
                    duration: trailerObj.duration || 0,
                  }
                )
              : null,
            heroImage: heroImageObj
              ? Object.assign(
                  new File([""], heroImageObj.fileName, {
                    type: `image/${
                      heroImageObj.fileName.split(".").pop()?.toLowerCase() ||
                      "png"
                    }`,
                    lastModified: Date.now(),
                  }),
                  {
                    isPlaceholder: true,
                    originalSize: heroImageObj.size
                      ? heroImageObj.size * 1024 * 1024
                      : 0,
                    width: heroImageObj.width || 1920,
                    height: heroImageObj.height || 1080,
                  }
                )
              : null,
            thumbnail: thumbnailObj
              ? Object.assign(
                  new File([""], thumbnailObj.fileName, {
                    type: `image/${
                      thumbnailObj.fileName.split(".").pop()?.toLowerCase() ||
                      "png"
                    }`,
                    lastModified: Date.now(),
                  }),
                  {
                    isPlaceholder: true,
                    originalSize: thumbnailObj.size
                      ? thumbnailObj.size * 1024 * 1024
                      : 0,
                    width: thumbnailObj.width || 400,
                    height: thumbnailObj.height || 400,
                  }
                )
              : null,
            subtitles:
              asset.subtitles && Array.isArray(asset.subtitles)
                ? asset.subtitles.map((s: any) =>
                    Object.assign(
                      new File([""], s.fileName, {
                        type: "text/srt",
                        lastModified: Date.now(),
                      }),
                      {
                        language: s.language,
                        isPlaceholder: true,
                        subtitleId: s.id?.toString(),
                        originalSize: s.size ? s.size * 1024 * 1024 : 0,
                      }
                    )
                  )
                : [],
          });
        })
        .catch((err) => {
          if (active) setDetailError(parseApiError(err, "Could not load this title. Reload before editing."));
        })
        .finally(() => { if (active) setLoadingDetail(false); });
      return () => { active = false; };
    }
  }, [editingAssetId]);

  const hasChanges = React.useMemo(() => {
    // Check if any metadata field is non-empty
    const metadataChanged = Object.values(metadata).some(
      (value) => value.trim() !== ""
    );

    // Check if any file field is non-null
    const filesChanged = Object.values(files).some((file) => {
      if (Array.isArray(file)) {
        return file.length > 0;
      }
      return file !== null;
    });

    // Check if any asset or file IDs array has items
    const assetIdsChanged =
      assetAndFileIds.videoIds.length > 0 ||
      assetAndFileIds.imageIds.length > 0 ||
      assetAndFileIds.subtitleIds.length > 0;

    return metadataChanged || filesChanged || assetIdsChanged;
  }, [metadata, files, assetAndFileIds]);

  // Warn before closing the tab. The draft asset is intentionally NOT deleted
  // here (or on unmount): doing so destroys in-progress drafts and breaks
  // React StrictMode's mount/unmount/mount cycle. Drafts are only removed by an
  // explicit "Discard".
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasChanges || (!editingAssetId && assetAndFileIds.assetId)) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges, editingAssetId, assetAndFileIds.assetId]);

  const handleDiscardClick = async () => {
    if (hasChanges) {
      setShowDiscardModal(true);
    } else {
      // Even if no changes, remove the asset if it's a new upload (not editing) and not saving
      if (
        !editingAssetId &&
        assetAndFileIds.assetId &&
        !isSavingDraftRef.current &&
        !assetDeletedRef.current
      ) {
        const token = getCookie("userToken");
        if (token) {
          try {
            assetDeletedRef.current = true; // Mark as deleted to prevent double deletion
            await removeAsset(assetAndFileIds.assetId);
            console.log("Asset removed successfully on discard (no changes)");
          } catch (error) {
            console.error(
              "Failed to remove asset on discard (no changes):",
              error
            );
            // Continue with navigation even if asset removal fails
          }
        }
      }
      navigate("/home/library");
    }
  };

  // Function to handle navigation with asset cleanup
  const handleNavigationWithCleanup = async (targetPath: string) => {
    // Remove asset if it's a new upload (not editing) and we're not saving
    if (
      !editingAssetId &&
      assetAndFileIds.assetId &&
      !isSavingDraftRef.current &&
      !assetDeletedRef.current
    ) {
      const token = getCookie("userToken");
      if (token) {
        try {
          assetDeletedRef.current = true; // Mark as deleted to prevent double deletion
          await removeAsset(assetAndFileIds.assetId);
          console.log("Asset removed successfully on navigation");
        } catch (error) {
          console.error("Failed to remove asset on navigation:", error);
          // Continue with navigation even if asset removal fails
        }
      }
    }
    navigate(targetPath);
  };

  const cancelDiscard = () => setShowDiscardModal(false);
  const confirmDiscard = async () => {
    // Only remove the asset if we're not editing an existing asset and not saving
    if (
      !editingAssetId &&
      assetAndFileIds.assetId &&
      !isSavingDraftRef.current &&
      !assetDeletedRef.current
    ) {
      const token = getCookie("userToken");
      if (token) {
        try {
          assetDeletedRef.current = true; // Mark as deleted to prevent double deletion
          await removeAsset(assetAndFileIds.assetId);
          console.log("Asset removed successfully on discard");
        } catch (error) {
          console.error("Failed to remove asset on discard:", error);
          // Continue with navigation even if asset removal fails
        }
      }
    }
    navigate("/home/library");
  };

  const handleSubmitClick = () => {
    if (savingBlocked) return;
    // Define required fields
    const requiredFields = [
      "title",
      "language",
      "category",
      "genre",
      "description",
      "format",
      "ageRating",
      "copyrightOwner",
    ];

    // Check if any required field is empty
    const hasErrors = requiredFields.some((field) => {
      const isEmpty = !metadata[field as keyof typeof metadata]?.trim();
      if (isEmpty) {
        setValidationErrors((prev) => ({ ...prev, [field]: true }));
        setTouchedFields((prev) => ({ ...prev, [field]: true }));
      }
      return isEmpty;
    });

    // Main video and hero image must be fully uploaded, not merely selected.
    if (!files.mainVideo || uploadStages.mainVideo !== "uploaded") {
      setValidationErrors((prev) => ({ ...prev, mainVideo: true }));
      if (files.mainVideo) {
        alert(
          "The main video is still uploading. Please wait for the upload to finish before submitting."
        );
      }
      return;
    }

    if (!files.heroImage || uploadStages.heroImage !== "uploaded") {
      setValidationErrors((prev) => ({ ...prev, heroImage: true }));
      if (files.heroImage) {
        alert(
          "The hero image is still uploading. Please wait for the upload to finish before submitting."
        );
      }
      return;
    }

    if (hasErrors) {
      return;
    }

    setShowSubmitModal(true);
  };
  const cancelSubmit = () => setShowSubmitModal(false);
  const confirmSubmit = async () => {
    const submitted = await handleSubmit();
    if (!submitted) {
      return;
    }
    isSavingDraftRef.current = true;
    setShowSubmitModal(false);
    alert("Title submitted successfully!");
    navigate("/home/library");
  };

  const handleSaveDraft = async () => {
    if (savingBlocked) return;
    const token = getCookie("userToken");
    if (!token) {
      alert("Please log in to save draft");
      window.location.href = "/login";
      return;
    }

    const userId = parseInt(getCookie("userId") || "0"); // Fallback to 0 if not found

    if (!metadata.title || metadata.title.trim() === "") {
      alert("Title is required");
      return;
    }

    const payload = {
      title: metadata.title || "",
      language: metadata.language || "",
      category: metadata.category || "",
      genre: metadata.genre || "",
      description: metadata.description || "",
      format: metadata.format || "",
      ageRating: metadata.ageRating || "",
      comfortLevel: metadata.comfortLevel || "",
      copyrightOwner: metadata.copyrightOwner || "",
      publisher: metadata.publisher || "",
      releaseDate: metadata.releaseDate
        ? new Date(metadata.releaseDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      festivalParticipations: metadata.festivalParticipations || "",
      awards: metadata.awards || "",
      userId: userId,
      assetId: parseInt(assetAndFileIds.assetId) || 0,
      videoIds: assetAndFileIds.videoIds.map((id) => parseInt(id)),
      imageIds: assetAndFileIds.imageIds.map((id) => parseInt(id)),
      subtitleIds: assetAndFileIds.subtitleIds.map((id) => parseInt(id)),
    };

    try {
      await assetApi.postApiAssetSaveDraft(payload as any);

      console.log("Setting isSavingDraft to true");
      isSavingDraftRef.current = true;
      // Set flag to prevent asset removal on navigation
      alert("Draft saved successfully!");
      navigate("/home/library");
    } catch (error) {
      console.error("Error saving draft:", error);
      alert(error instanceof Error ? error.message : "Failed to save draft");
    }

    console.log("Payload being sent to save-draft:", payload);
  };

  /** Resolves true only when the backend accepted the submission. */
  const handleSubmit = async (): Promise<boolean> => {
    if (savingBlocked) return false;
    const token = getCookie("userToken");
    if (!token) {
      alert("Please log in to submit content");
      window.location.href = "/login";
      return false;
    }

    const userId = getCookie("userId");
    if (!userId) {
      alert("User ID not found. Please log in again.");
      window.location.href = "/login";
      return false;
    }

    const payload = {
      title: metadata.title || "",
      language: metadata.language || "",
      category: metadata.category || "",
      genre: metadata.genre || "",
      description: metadata.description || "",
      format: metadata.format || "",
      ageRating: metadata.ageRating || "",
      comfortLevel: metadata.comfortLevel || "",
      copyrightOwner: metadata.copyrightOwner || "",
      publisher: metadata.publisher || "",
      releaseDate: metadata.releaseDate
        ? new Date(metadata.releaseDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      festivalParticipations: metadata.festivalParticipations || "",
      awards: metadata.awards || "",
      userId: userId,
      assetId: parseInt(assetAndFileIds.assetId) || 0,
      videoIds: assetAndFileIds.videoIds.map((id) => parseInt(id)),
      imageIds: assetAndFileIds.imageIds.map((id) => parseInt(id)),
      subtitleIds: assetAndFileIds.subtitleIds.map((id) => parseInt(id)),
    };

    try {
      await assetApi.postApiAssetSubmit(payload as any);
      return true;
    } catch (error: any) {
      console.error("Error submitting title:", error);
      const data = error?.response?.data;
      const message =
        (typeof data === "string" && data) ||
        data?.error ||
        data?.message ||
        data?.title ||
        (error instanceof Error ? error.message : "") ||
        "Failed to submit title";
      alert(String(message));
      return false;
    }
  };

  const handleSaveChanges = async () => {
    if (savingBlocked) return;
    const token = getCookie("userToken");
    if (!token) {
      alert("Please log in to save changes");
      window.location.href = "/login";
      return;
    }

    const userId = getCookie("userId");
    if (!userId) {
      alert("User ID not found. Please log in again.");
      window.location.href = "/login";
      return;
    }

    const payload = {
      title: metadata.title || "",
      language: metadata.language || "",
      category: metadata.category || "",
      genre: metadata.genre || "",
      description: metadata.description || "",
      format: metadata.format || "",
      ageRating: metadata.ageRating || "",
      comfortLevel: metadata.comfortLevel || "",
      copyrightOwner: metadata.copyrightOwner || "",
      publisher: metadata.publisher || "",
      releaseDate: metadata.releaseDate || "",
      festivalParticipations: metadata.festivalParticipations || "",
      awards: metadata.awards || "",
    };

    console.log("Sending payload:", payload);

    try {
      await assetApi.putApiAssetEditAsetAssetId(
        Number.parseInt(assetAndFileIds.assetId, 10),
        payload as any
      );

      alert("Changes saved successfully!");
      navigate("/home/library");
    } catch (error) {
      console.error("Error saving changes:", error);
      alert(error instanceof Error ? error.message : "Failed to save changes");
    }
  };

  const handleAssetAndFileIdsUpdate = React.useCallback((
    newIds: Partial<typeof assetAndFileIds>
  ) => {
    setAssetAndFileIds((prev) => ({
      ...prev,
      ...newIds,
    }));
  }, []);

  const handleMetadataChange = (
    field: keyof typeof metadata,
    value: string
  ) => {
    setMetadata((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleFieldBlur = (field: string) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  return (
    <div className="upload-container">
      {detailError && <div role="alert" className="password-error-box">{detailError}</div>}
      <UploadHeader
        savingDisabled={savingBlocked}
        onDiscardClick={handleDiscardClick}
        onSaveDraft={handleSaveDraft}
        onSubmitClick={handleSubmitClick}
        onSaveChanges={handleSaveChanges}
        onNavigationWithCleanup={handleNavigationWithCleanup}
        hasChanges={hasChanges}
        assetStatus={assetStatus}
        isEditing={!!editingAssetId}
      />
      <div className="upload-flow-container">
        <UploadFile
          files={files}
          onFileChange={handleFileChange}
          onAssetAndFileIdsUpdate={handleAssetAndFileIdsUpdate}
          assetId={assetAndFileIds.assetId}
          mainVideoId={assetAndFileIds.mainVideo}
          trailerId={assetAndFileIds.trailer}
          heroImageId={assetAndFileIds.heroImage}
          thumbnailId={assetAndFileIds.thumbnail}
          videoIds={assetAndFileIds.videoIds}
          subtitleIds={assetAndFileIds.subtitleIds}
          isMainVideoInvalid={validationErrors.mainVideo}
          isHeroImageInvalid={validationErrors.heroImage}
          isEditingPublished={
            Boolean(editingAssetId) && assetStatus === "Published"
          }
          vrFormat={metadata.format}
          onUploadStagesChange={handleUploadStagesChange}
        />
        <div className="upload-flow-divider"></div>
        <NewTitleMetadata
          metadata={metadata}
          onMetadataChange={handleMetadataChange}
          onBlur={handleFieldBlur}
          touchedFields={touchedFields}
        />
      </div>
      {showDiscardModal && (
        <div className="uploadflow-modal-overlay">
          <div className="uploadflow-modal">
            <div className="uploadflow-modal-header">
              <div className="uploadflow-modal-header-content">
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.9836 0.502678C8.77744 0.555166 5.72075 1.86728 3.47427 4.15538C2.35024 5.27447 1.46338 6.60869 0.866705 8.07831C0.270027 9.54793 -0.0242179 11.1228 0.00155827 12.7087C-0.000286057 14.2583 0.303697 15.7931 0.896087 17.2251C1.48848 18.6571 2.35763 19.958 3.45373 21.0535C4.54983 22.1489 5.85133 23.0173 7.28365 23.6089C8.71596 24.2004 10.2509 24.5035 11.8006 24.5007H12.0146C15.2217 24.4677 18.2847 23.1638 20.5316 20.8751C22.7785 18.5864 24.0257 15.4999 23.9996 12.2927C24.0033 10.7243 23.6933 9.1709 23.0879 7.72398C22.4825 6.27706 21.5939 4.9658 20.4744 3.86731C19.3548 2.76882 18.0269 1.90527 16.5688 1.32745C15.1106 0.749636 13.5517 0.469214 11.9836 0.502678ZM10.5007 17.0433C10.4935 16.8473 10.5254 16.6517 10.5947 16.4682C10.6639 16.2846 10.7691 16.1167 10.904 15.9742C11.0389 15.8318 11.2009 15.7177 11.3804 15.6386C11.5599 15.5594 11.7534 15.5169 11.9496 15.5135H11.9766C12.3712 15.5142 12.7501 15.6677 13.034 15.9417C13.3179 16.2157 13.4847 16.589 13.4995 16.9833C13.5068 17.1794 13.4749 17.375 13.4057 17.5586C13.3365 17.7423 13.2314 17.9102 13.0965 18.0527C12.9615 18.1952 12.7995 18.3093 12.6199 18.3884C12.4403 18.4674 12.2468 18.5099 12.0506 18.5132H12.0236C11.6291 18.5119 11.2505 18.3583 10.9667 18.0844C10.6829 17.8105 10.5159 17.4375 10.5007 17.0433ZM11.0007 13.0017V7.00215C11.0007 6.73695 11.106 6.48262 11.2935 6.2951C11.481 6.10758 11.7354 6.00223 12.0006 6.00223C12.2658 6.00223 12.5201 6.10758 12.7076 6.2951C12.8951 6.48262 13.0005 6.73695 13.0005 7.00215V13.0017C13.0005 13.2669 12.8951 13.5212 12.7076 13.7087C12.5201 13.8962 12.2658 14.0016 12.0006 14.0016C11.7354 14.0016 11.481 13.8962 11.2935 13.7087C11.106 13.5212 11.0007 13.2669 11.0007 13.0017Z"
                    fill="#EF4444"
                  />
                </svg>

                <h2 className="uploadflow-modal-title">Discard changes?</h2>
              </div>
            </div>
            <div className="uploadflow-modal-support">
              <p className="uploadflow-modal-text">
                If you discard changes, all progress will be lost. This action
                cannot be undone.
              </p>
            </div>
            <div className="uploadflow-modal-footer">
              <button
                className="footer-modal-cancel-btn"
                onClick={cancelDiscard}
              >
                Back to editing
              </button>
              <button
                className="discard-modal-confirm-btn"
                onClick={confirmDiscard}
              >
                Discard and exit
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubmitModal && (
        <div className="uploadflow-modal-overlay">
          <div className="uploadflow-modal">
            <div className="uploadflow-modal-header">
              <div className="uploadflow-modal-header-content">
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.9836 0.502678C8.77744 0.555166 5.72075 1.86728 3.47427 4.15538C2.35024 5.27447 1.46338 6.60869 0.866705 8.07831C0.270027 9.54793 -0.0242179 11.1228 0.00155827 12.7087C-0.000286057 14.2583 0.303697 15.7931 0.896087 17.2251C1.48848 18.6571 2.35763 19.958 3.45373 21.0535C4.54983 22.1489 5.85133 23.0173 7.28365 23.6089C8.71596 24.2004 10.2509 24.5035 11.8006 24.5007H12.0146C15.2217 24.4677 18.2847 23.1638 20.5316 20.8751C22.7785 18.5864 24.0257 15.4999 23.9996 12.2927C24.0033 10.7243 23.6933 9.1709 23.0879 7.72398C22.4825 6.27706 21.5939 4.9658 20.4744 3.86731C19.3548 2.76882 18.0269 1.90527 16.5688 1.32745C15.1106 0.749636 13.5517 0.469214 11.9836 0.502678ZM10.5007 17.0433C10.4935 16.8473 10.5254 16.6517 10.5947 16.4682C10.6639 16.2846 10.7691 16.1167 10.904 15.9742C11.0389 15.8318 11.2009 15.7177 11.3804 15.6386C11.5599 15.5594 11.7534 15.5169 11.9496 15.5135H11.9766C12.3712 15.5142 12.7501 15.6677 13.034 15.9417C13.3179 16.2157 13.4847 16.589 13.4995 16.9833C13.5068 17.1794 13.4749 17.375 13.4057 17.5586C13.3365 17.7423 13.2314 17.9102 13.0965 18.0527C12.9615 18.1952 12.7995 18.3093 12.6199 18.3884C12.4403 18.4674 12.2468 18.5099 12.0506 18.5132H12.0236C11.6291 18.5119 11.2505 18.3583 10.9667 18.0844C10.6829 17.8105 10.5159 17.4375 10.5007 17.0433ZM11.0007 13.0017V7.00215C11.0007 6.73695 11.106 6.48262 11.2935 6.2951C11.481 6.10758 11.7354 6.00223 12.0006 6.00223C12.2658 6.00223 12.5201 6.10758 12.7076 6.2951C12.8951 6.48262 13.0005 6.73695 13.0005 7.00215V13.0017C13.0005 13.2669 12.8951 13.5212 12.7076 13.7087C12.5201 13.8962 12.2658 14.0016 12.0006 14.0016C11.7354 14.0016 11.481 13.8962 11.2935 13.7087C11.106 13.5212 11.0007 13.2669 11.0007 13.0017Z"
                    fill="#aeaea0"
                  />
                </svg>

                <h2 className="uploadflow-modal-title">
                  Submit for publishing?
                </h2>
              </div>
            </div>
            <div className="uploadflow-modal-support">
              <p className="uploadflow-modal-text">
                If you proceed with submitting, you won't be able to edit your
                content details until the review process is over.
              </p>
            </div>
            <div className="uploadflow-modal-footer">
              <button
                className="footer-modal-cancel-btn"
                onClick={cancelSubmit}
              >
                Back to editing
              </button>
              <button
                className="submit-modal-confirm-btn"
                disabled={savingBlocked}
                onClick={confirmSubmit}
              >
                Submit now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadNewTitle;
